import { MotionConfig } from 'framer-motion';
import { useEffect, type ReactNode } from 'react';
import { RouterProvider } from 'react-router';
import { ApiProvider } from './api/query';
import { Toaster, TooltipProvider } from './components/ui';
import { useSession } from './lib/session';
import { useAppStore } from './lib/store';
import { router } from './routes';

/** Applies theme + reduced-motion preferences to <html> (tokens.css keys off these attributes). */
const ThemeEffect = () => {
  const theme = useAppStore((s) => s.theme);
  const reducedMotion = useAppStore((s) => s.reducedMotion);
  useEffect(() => {
    const el = document.documentElement;
    if (theme === 'system') delete el.dataset.theme;
    else el.dataset.theme = theme;
    el.dataset.reducedMotion = String(reducedMotion);
  }, [theme, reducedMotion]);
  return null;
};

/** When a user signs in, their saved preferences win over whatever this browser had. */
const PreferencesSync = () => {
  const { user } = useSession();
  const set = useAppStore((s) => s.set);
  useEffect(() => {
    if (!user) return;
    set({ theme: user.theme, calmMode: user.calmMode, reducedMotion: user.reducedMotion, privacyBlur: user.privacyBlur });
    // only on sign-in / user switch, not on every refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  return null;
};

export const Providers = ({ children }: { readonly children: ReactNode }) => {
  const reducedMotion = useAppStore((s) => s.reducedMotion);
  return (
    <ApiProvider>
      <MotionConfig reducedMotion={reducedMotion ? 'always' : 'user'}>
        <TooltipProvider delayDuration={300}>
          <ThemeEffect />
          <PreferencesSync />
          {children}
          <Toaster />
        </TooltipProvider>
      </MotionConfig>
    </ApiProvider>
  );
};

export const App = () => (
  <Providers>
    <RouterProvider router={router} />
  </Providers>
);
